import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Search, RotateCcw, Save, Loader2, X, Check, AlertCircle,
    Clock, Calendar, User, DollarSign, Info, Plus, Pencil,
    List, Filter, RefreshCw, Trash2, Copy, Users, ArrowRight, ArrowLeft,
    UserCheck, UsersRound,
} from 'lucide-react';

import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import {
    // fetchOTEntryPayrollPeriods,
    fetchOvertimeList,
    addOvertime,
    updateOvertime,
    clearOTEntryStatus,
    fetchEmployeeOtRates,
    selectEmployeeOtRates,
    selectOtRatesLoading,
    // selectOTEntryPeriods,
    // selectOTEntryPeriodsLoading,
    selectOTEntrySubmitting,
    selectOTEntryError,
    selectOTEntrySuccess,
    selectOTEntryList,
    selectOTEntryListLoading,
} from '../../store/slices/OTEntrySlice';
import DatePickerWithFormat from '../../components/common/DatePickerWithFormat';
import SearchableSelect from '../../components/common/SearchableSelect';
import TimeField24 from '../../components/common/Timefield24';

// ── Style constants ────────────────────────────────────────────────────────
const FIELD =
    'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700 disabled:bg-gray-50 disabled:text-gray-400';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';
const SECTION = 'bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4';
const SECTION_HEADER = 'flex items-center gap-2 text-gray-700 text-sm font-semibold';

const OT_TYPES = [
    { value: 'WEEK_DAY_1700', label: 'Week Day (17.00)' },
    { value: 'WEEK_DAY_1630', label: 'Week Day (16.30)' },
    { value: 'SATURDAY', label: 'Saturday' },
    { value: 'SUNDAY', label: 'Sunday' },
    { value: 'HOLIDAY', label: 'Poya/Holiday/Mercantile' },
];
const OT_TYPE_LABEL = (v) => OT_TYPES.find((t) => t.value === v)?.label || v || '—';

const OT_TYPE_DEFAULT_START = {
    WEEK_DAY_1700: '17:00',
    WEEK_DAY_1630: '16:30',
    SATURDAY: '14:00',
    SUNDAY: '08:00',
    HOLIDAY: '08:00',
};

const OT_TYPE_DEFAULT_RATE = {
    WEEK_DAY_1700: 1.5,
    WEEK_DAY_1630: 1.5,
    SATURDAY: 1.5,
    SUNDAY: 1.5,
    HOLIDAY: 2,
    
};

const initialForm = {
    overtimeId: '', payrollId: '', employeeId: '', payrollPeriodId: '',
    otDate: '',
    otType: 'WEEK_DAY_1700',
    startTime: OT_TYPE_DEFAULT_START['WEEK_DAY_1700'],
    endTime: '',
    totalHours: '',
    otRate: String(OT_TYPE_DEFAULT_RATE['T']),
    otAmount: '',
};

// ── Helpers ────────────────────────────────────────────────────────────────
const toMinutes = (t) => {
    if (!t) return null;
    const [h, m] = String(t).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
};

const calcHours = (start, end) => {
    const s = toMinutes(start);
    const e = toMinutes(end);
    if (s === null || e === null) return 0;
    let diff = e - s;
    if (diff < 0) diff += 24 * 60;
    return Math.round((diff / 60) * 100) / 100;
};

/** Adjusted OT Hours = hours × OT-type rate (sent as p_adjHours). */
const calcAdjHours = (hours, rate) => {
    const h = Number(hours) || 0;
    const r = Number(rate) || 0;
    return h > 0 && r > 0 ? String(Math.round(h * r * 100) / 100) : '';
};

const validateForm = (form) => {
    const v = {};
    if (!form.employeeId) v.employeeId = 'Employee is required';
    // if (!form.payrollPeriodId) v.payrollPeriodId = 'Payroll period is required';
    if (!form.otDate) v.otDate = 'OT date is required';
    if (!form.otType) v.otType = 'OT type is required';
    if (!form.startTime) v.startTime = 'Start time is required';
    if (!form.endTime) v.endTime = 'End time is required';
    if (!Number(form.totalHours)) v.totalHours = 'Total hours must be > 0';
    if (!Number(form.otRate)) v.otRate = 'OT rate must be > 0';

    // const period = payrollPeriods.find((p) => String(p.id) === String(form.payrollPeriodId));
    // if (period && form.otDate) {
    //     const d = new Date(form.otDate);
    //     const s = period.otStartDate ? new Date(period.otStartDate) : null;
    //     const e = period.otEndDate ? new Date(period.otEndDate) : null;
    //     if (s && !Number.isNaN(s.getTime()) && d < s)
    //         v.otDate = `Date is before period OT start (${period.otStartDate})`;
    //     if (e && !Number.isNaN(e.getTime()) && d > e)
    //         v.otDate = `Date is after period OT end (${period.otEndDate})`;
    // }
    return v;
};

const validateOTRow = (row) => {
    const v = {};
    // if (!row.payrollPeriodId) v.payrollPeriodId = 'Payroll period is required';
    if (!row.otDate) v.otDate = 'OT date is required';
    if (!row.otType) v.otType = 'OT type is required';
    if (!row.startTime) v.startTime = 'Start time is required';
    if (!row.endTime) v.endTime = 'End time is required';
    if (!Number(row.totalHours)) v.totalHours = 'Total hours must be > 0';
    if (!Number(row.otRate)) v.otRate = 'OT rate must be > 0';

    // const period = payrollPeriods.find((p) => String(p.id) === String(row.payrollPeriodId));
    // if (period && row.otDate) {
    //     const d = new Date(row.otDate);
    //     const s = period.otStartDate ? new Date(period.otStartDate) : null;
    //     const e = period.otEndDate ? new Date(period.otEndDate) : null;
    //     if (s && !Number.isNaN(s.getTime()) && d < s)
    //         v.otDate = `Date is before period OT start (${period.otStartDate})`;
    //     if (e && !Number.isNaN(e.getTime()) && d > e)
    //         v.otDate = `Date is after period OT end (${period.otEndDate})`;
    // }
    return v;
};

const formatEmployeeLabel = (emp) =>
    emp ? (emp.serviceNo ? `${emp.serviceNo} — ${emp.name}` : emp.name) : '';

const employeeMatchesId = (emp, rawId) => {
    if (!emp || rawId === undefined || rawId === null || rawId === '') return false;
    const target = String(rawId);
    return (
        String(emp.employeeId ?? '') === target ||
        String(emp.serviceNo ?? '') === target ||
        String(emp.id ?? '') === target
    );
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const s = String(iso).slice(0, 10);
    const [y, m, d] = s.split('-');
    if (!y || !m || !d) return iso;
    return `${y}/${m}/${d}`;
};

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// const formatPeriodLabel = (period) => {
//     if (!period) return '—';
//     const start = period.otStartDate || period.startDate;
//     const end = period.otEndDate || period.endDate;
//
//     let year = '';
//     let month = '';
//     let range = '';
//
//     if (start) {
//         const [sy, sm, sd] = String(start).slice(0, 10).split('-');
//         year = sy;
//         month = MONTH_ABBR[Number(sm) - 1] || sm;
//         range = `${sm}/${sd}`;
//     }
//     if (end) {
//         const [, em, ed] = String(end).slice(0, 10).split('-');
//         range = range ? `${range} - ${em}/${ed}` : `${em}/${ed}`;
//     }
//
//     const title = year && month
//         ? `${year} ${month}`
//         : (period.name || period.code || '—');
//
//     return range ? `${title} (${range})` : title;
// };

const makeId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const emptyOTRow = (overrides = {}) => ({
    rowId: makeId(),
    payrollPeriodId: '',
    otDate: '',
    otType: 'WEEK_DAY_1700',
    startTime: OT_TYPE_DEFAULT_START['WEEK_DAY_1700'],
    endTime: '',
    totalHours: '',
    otRate: String(OT_TYPE_DEFAULT_RATE['WEEK_DAY_1700']),
    otAmount: '',
    adjHours: '',        // Adjusted OT Hours = hours × rate → p_adjHours
    ...overrides,
});

const mapReduxEmployees = (reduxEmployees) =>
    (Array.isArray(reduxEmployees) ? reduxEmployees : [])
        .map((emp) => ({
            employeeId: String(emp.HED_EMPLOYEE_ID || emp.HED_SERVICE_NO || ''),
            serviceNo: emp.HED_SERVICE_NO || '',
            name:
                emp.HED_REPORT_NAME ||
                `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`.trim() ||
                `Employee ${emp.HED_SERVICE_NO}`,
            department: emp.HDD_DEPARTMENT_NAME || '',
        }))
        .filter((e) => e.employeeId);

// ── Searchable payroll-period dropdown ─────────────────────────────────────
// const PeriodSelect = ({
//     value, onChange, periods = [], getLabel = formatPeriodLabel,
//     emptyLabel = 'Select', loading = false, loadingLabel = 'Loading...',
//     className = FIELD, disabled = false,
// }) => (
//     <SearchableSelect
//         value={value}
//         onChange={onChange}
//         options={periods.map((p) => ({
//             value: p.id,
//             label: getLabel(p),
//             searchText: `${p.name || ''} ${p.code || ''}`,
//         }))}
//         emptyOption={{ value: '', label: emptyLabel }}
//         placeholder={emptyLabel}
//         loading={loading}
//         loadingLabel={loadingLabel}
//         disabled={disabled}
//         searchPlaceholder="Search period..."
//         className={className}
//     />
// );

// ── Employee search dropdown ───────────────────────────────────────────────
const EmployeeSearch = ({
    value, onSelect, employees = [], loading = false, disabled = false,
    placeholder = 'Search by service no or name...',
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const wrapperRef = useRef(null);

    useEffect(() => {
        setSearchTerm(formatEmployeeLabel(value));
    }, [value?.employeeId, value?.serviceNo, value?.name]);

    useEffect(() => {
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                closeAndReconcile();
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [value, searchTerm]);

    useEffect(() => {
        const term = searchTerm.toLowerCase().trim();
        const isDisplay = value && searchTerm === formatEmployeeLabel(value);
        if (!term || isDisplay) {
            setFilteredEmployees(employees);
            return;
        }
        const filtered = employees.filter(
            (emp) =>
                emp.employeeId?.toLowerCase().includes(term) ||
                emp.serviceNo?.toLowerCase().includes(term) ||
                emp.name?.toLowerCase().includes(term)
        );
        setFilteredEmployees(filtered);
    }, [searchTerm, employees, value]);

    const closeAndReconcile = () => {
        setIsOpen(false);
        const validLabel = formatEmployeeLabel(value);
        if (searchTerm !== validLabel) setSearchTerm(validLabel);
    };

    const handleSelectEmployee = (emp) => {
        setSearchTerm(formatEmployeeLabel(emp));
        setIsOpen(false);
        onSelect(emp);
    };

    const handleClear = () => {
        setSearchTerm('');
        setIsOpen(false);
        onSelect(null);
    };

    return (
        <div ref={wrapperRef} className="relative">
            <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                    type="text" value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        if (!e.target.value.trim()) onSelect(null);
                    }}
                    onFocus={() => !disabled && setIsOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.currentTarget.blur();
                            closeAndReconcile();
                        }
                    }}
                    placeholder={placeholder} disabled={disabled}
                    className="w-full h-9 bg-white border border-gray-200 rounded-lg text-xs pl-8 pr-8 outline-none focus:border-primary-500 text-gray-700 disabled:bg-gray-50 disabled:text-gray-400"
                />
                {searchTerm && !disabled && (
                    <button type="button" onClick={handleClear}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X size={14} />
                    </button>
                )}
            </div>
            {loading && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center text-xs text-gray-500">
                    Loading employees...
                </div>
            )}
            {isOpen && !loading && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {filteredEmployees.length > 0 ? (
                        filteredEmployees.map((emp) => (
                            <div key={emp.serviceNo || emp.employeeId}
                                onClick={() => handleSelectEmployee(emp)}
                                className={`px-3 py-2 hover:bg-primary-50 cursor-pointer border-b border-gray-100 last:border-0 ${value?.serviceNo === emp.serviceNo ? 'bg-primary-50' : ''}`}>
                                <div className="flex items-center gap-2.5">
                                    <span className="text-[11px] font-mono text-gray-400 w-20 shrink-0">{emp.serviceNo || '—'}</span>
                                    <span className="text-xs text-gray-800 truncate">{emp.name}</span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="px-3 py-2.5 text-xs text-gray-400">No matching employees</div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Multi-employee picker ──────────────────────────────────────────────────
const MultiEmployeePicker = ({ employees, loading, selectedIds, onToggle, onSelectAll, onClearAll }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filtered = useMemo(() => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return employees;
        return employees.filter(
            (emp) =>
                emp.employeeId?.toLowerCase().includes(term) ||
                emp.serviceNo?.toLowerCase().includes(term) ||
                emp.name?.toLowerCase().includes(term)
        );
    }, [employees, searchTerm]);

    const allVisibleSelected = filtered.length > 0 && filtered.every((e) => selectedIds.has(e.employeeId));

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                        type="text" value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search employees..."
                        className="w-full h-9 bg-white border border-gray-200 rounded-lg text-xs pl-8 pr-3 outline-none focus:border-primary-500 text-gray-700"
                    />
                </div>
                <button type="button"
                    onClick={() => (allVisibleSelected ? onClearAll() : onSelectAll(filtered))}
                    className="h-9 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors">
                    {allVisibleSelected ? 'Clear visible' : 'Select all visible'}
                </button>
                <button type="button" onClick={onClearAll}
                    className="h-9 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors">
                    Clear all
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 size={22} className="animate-spin text-primary-600" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="py-8 text-center text-xs text-gray-400">No matching employees</div>
            ) : (
                <div className="max-h-[380px] overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-100">
                    {filtered.map((emp) => {
                        const checked = selectedIds.has(emp.employeeId);
                        return (
                            <label key={emp.serviceNo || emp.employeeId}
                                className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${checked ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                                <input type="checkbox" checked={checked} onChange={() => onToggle(emp)}
                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                                <span className="text-[11px] font-mono text-gray-400 w-20 shrink-0">{emp.serviceNo || '—'}</span>
                                <span className="text-xs text-gray-800 truncate flex-1">{emp.name}</span>
                                {emp.department && (
                                    <span className="text-[11px] text-gray-400 truncate max-w-[140px]">{emp.department}</span>
                                )}
                            </label>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ── Selected employee chips ────────────────────────────────────────────────
const SelectedChips = ({ employees, onRemove, onClear }) => {
    if (!employees.length) return null;
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {employees.map((emp) => (
                <span key={emp.employeeId}
                    className="inline-flex items-center gap-1.5 bg-primary-50 border border-primary-100 text-primary-700 text-[11px] font-medium rounded-full pl-2.5 pr-1 py-1">
                    <span className="font-mono text-primary-500">{emp.serviceNo}</span>
                    <span className="truncate max-w-[140px]">{emp.name}</span>
                    <button type="button" onClick={() => onRemove(emp.employeeId)}
                        className="rounded-full p-0.5 hover:bg-primary-100 text-primary-500"
                        aria-label={`Remove ${emp.name}`}>
                        <X size={11} />
                    </button>
                </span>
            ))}
            <button type="button" onClick={onClear}
                className="text-[11px] font-semibold text-gray-400 hover:text-gray-600 px-2">
                Clear all
            </button>
        </div>
    );
};

// ── Stepper ────────────────────────────────────────────────────────────────
const Stepper = ({ step }) => {
    const steps = [
        { n: 1, label: 'OT Details', icon: Clock },
        { n: 2, label: 'Select Employees', icon: Users },
        { n: 3, label: 'Review & Save', icon: Check },
    ];
    return (
        <div className="flex items-center gap-2">
            {steps.map((s, i) => {
                const Icon = s.icon;
                const active = step === s.n;
                const done = step > s.n;
                return (
                    <React.Fragment key={s.n}>
                        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${active ? 'bg-primary-50 text-primary-700' : done ? 'text-green-600' : 'text-gray-400'}`}>
                            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${active ? 'bg-primary-600 text-white' : done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                {done ? <Check size={11} /> : s.n}
                            </span>
                            <Icon size={13} />
                            <span className="hidden sm:inline">{s.label}</span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`h-px w-6 ${step > s.n ? 'bg-green-400' : 'bg-gray-200'}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════
//  STEP 1: OT DETAILS (multi-row editor)
// ═════════════════════════════════════════════════════════════════════════
const OTDetailsStep = ({ rows, setRows, validationByRow }) => {
    const updateRow = (rowId, key, value) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.rowId !== rowId) return r;
                const next = { ...r, [key]: value };

                if (key === 'startTime' || key === 'endTime') {
                    const hrs = calcHours(next.startTime, next.endTime);
                    next.totalHours = hrs > 0 ? String(hrs) : '';
                }

                // Adjusted OT Hours = hours × OT-type rate (→ p_adjHours).
                // The money amount needs each employee's own OT rate, so it is
                // worked out per employee when saving, not here.
                if (
                    key === 'totalHours' ||
                    key === 'otRate' ||
                    key === 'startTime' ||
                    key === 'endTime'
                ) {
                    next.adjHours = calcAdjHours(next.totalHours, next.otRate);
                }
                return next;
            })
        );
    };

    const addRow = () => {
        setRows((prev) => {
            const last = prev[prev.length - 1];
            const nextType = last?.otType || 'T';
            return [
                ...prev,
                emptyOTRow({
                    // payrollPeriodId: last?.payrollPeriodId || '',
                    otType: nextType,
                    startTime: OT_TYPE_DEFAULT_START[nextType] || '',
                    otRate: String(OT_TYPE_DEFAULT_RATE[nextType] ?? ''),
                }),
            ];
        });
    };

    const duplicateRow = (rowId) => {
        setRows((prev) => {
            const idx = prev.findIndex((r) => r.rowId === rowId);
            if (idx === -1) return prev;
            const copy = { ...prev[idx], rowId: makeId() };
            const next = [...prev];
            next.splice(idx + 1, 0, copy);
            return next;
        });
    };

    const removeRow = (rowId) => {
        setRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.rowId !== rowId)));
    };

    const setOTType = (rowId, next) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.rowId !== rowId) return r;
                const nextRow = { ...r, otType: next };
                const suggestedStart = OT_TYPE_DEFAULT_START[next];
                if (suggestedStart) {
                    const prevSuggested = OT_TYPE_DEFAULT_START[r.otType];
                    if (!r.startTime || r.startTime === prevSuggested) {
                        nextRow.startTime = suggestedStart;
                    }
                }
                const suggestedRate = OT_TYPE_DEFAULT_RATE[next];
                if (suggestedRate !== undefined) {
                    const prevRate = OT_TYPE_DEFAULT_RATE[r.otType];
                    if (!r.otRate || Number(r.otRate) === Number(prevRate)) {
                        nextRow.otRate = String(suggestedRate);
                    }
                }
                // A new type can mean a new rate → refresh Adjusted OT Hours.
                nextRow.adjHours = calcAdjHours(nextRow.totalHours, nextRow.otRate);
                return nextRow;
            })
        );
    };

    const err = (rowId, key) => validationByRow?.[rowId]?.[key];
    const cellCls = (rowId, key) =>
        `w-full h-8 bg-white border rounded-md text-xs px-1.5 outline-none focus:border-primary-500 text-gray-700 ${err(rowId, key) ? 'border-red-400' : 'border-gray-200'}`;

    const totalHours = rows.reduce((sum, r) => sum + (Number(r.totalHours) || 0), 0);
    const totalAdjHours = rows.reduce((sum, r) => sum + (Number(r.adjHours) || 0), 0);

    return (
        <div className="space-y-4">
            <div className={SECTION}>
                <div className="flex items-center justify-between">
                    <div className={SECTION_HEADER}>
                        <Clock size={15} /> OT Details ({rows.length} row{rows.length === 1 ? '' : 's'})
                    </div>
                    <button type="button" onClick={addRow}
                        className="flex items-center gap-2 h-8 px-3 border border-dashed border-gray-300 text-gray-500 rounded-lg text-xs font-semibold hover:border-primary-400 hover:text-primary-600 transition-colors">
                        <Plus size={13} /> Add Row
                    </button>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-100">
                    <table className="w-full text-xs border-collapse">
                        <thead>
                            <tr className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                                {/* <th className="px-2.5 py-2 text-left font-semibold min-w-[150px]">Period</th> */}
                                <th className="px-2.5 py-2 text-left font-semibold min-w-[130px]">Date</th>
                                <th className="px-2.5 py-2 text-left font-semibold min-w-[120px]">Type</th>
                                <th className="px-2.5 py-2 text-left font-semibold min-w-[95px]">Start</th>
                                <th className="px-2.5 py-2 text-left font-semibold min-w-[95px]">End</th>
                                <th className="px-2.5 py-2 text-left font-semibold min-w-[85px]">Hours</th>
                                <th className="px-2.5 py-2 text-left font-semibold min-w-[85px]">Rate</th>
                                <th className="px-2.5 py-2 text-left font-semibold min-w-[110px]">Adjusted OT Hours</th>
                                <th className="px-2.5 py-2 min-w-[60px]"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.rowId} className="border-b border-gray-100 last:border-0">
                                    {/* <td className="px-2.5 py-1.5">
                                        <PeriodSelect value={row.payrollPeriodId}
                                            onChange={(v) => updateRow(row.rowId, 'payrollPeriodId', v)}
                                            className={cellCls(row.rowId, 'payrollPeriodId')}
                                            periods={payrollPeriods}
                                            loading={periodsLoading}
                                            loadingLabel="..."
                                            emptyLabel="Select" />
                                    </td> */}
                                    <td className="px-2.5 py-1.5">
                                        <DatePickerWithFormat value={row.otDate}
                                            onChange={(v) => updateRow(row.rowId, 'otDate', v)}
                                            className={cellCls(row.rowId, 'otDate')} />
                                    </td>
                                    <td className="px-2.5 py-1.5">
                                        <select value={row.otType}
                                            onChange={(e) => setOTType(row.rowId, e.target.value)}
                                            className={cellCls(row.rowId, 'otType')}>
                                            {OT_TYPES.map((t) => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-2.5 py-1.5">
                                        <TimeField24 value={row.startTime}
                                            onChange={(v) => updateRow(row.rowId, 'startTime', v)}
                                            hasError={!!err(row.rowId, 'startTime')} />
                                    </td>
                                    <td className="px-2.5 py-1.5">
                                        <TimeField24 value={row.endTime}
                                            onChange={(v) => updateRow(row.rowId, 'endTime', v)}
                                            hasError={!!err(row.rowId, 'endTime')} />
                                    </td>
                                    <td className="px-2.5 py-1.5">
                                        <input type="number" step="0.25" min="0" value={row.totalHours}
                                            onChange={(e) => updateRow(row.rowId, 'totalHours', e.target.value)}
                                            className={cellCls(row.rowId, 'totalHours')} />
                                    </td>
                                    <td className="px-2.5 py-1.5">
                                        <input type="number" step="0.01" min="0" value={row.otRate}
                                            readOnly disabled
                                            className="w-full h-8 bg-gray-50 border border-gray-200 rounded-md text-xs px-1.5 outline-none text-gray-500 cursor-not-allowed" />
                                    </td>
                                    <td className="px-2.5 py-1.5">
                                        <input type="number" value={row.adjHours}
                                            readOnly disabled title="Hours × Rate"
                                            className="w-full h-8 bg-gray-50 border border-gray-200 rounded-md text-xs px-1.5 outline-none text-gray-500 cursor-not-allowed" />
                                    </td>
                                    <td className="px-2.5 py-1.5 text-right whitespace-nowrap">
                                        <button type="button" onClick={() => duplicateRow(row.rowId)}
                                            title="Duplicate row"
                                            className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded">
                                            <Copy size={12} />
                                        </button>
                                        <button type="button" onClick={() => removeRow(row.rowId)}
                                            disabled={rows.length === 1} title="Remove row"
                                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400">
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {rows.some((r) => Object.values(validationByRow?.[r.rowId] || {}).some(Boolean)) && (
                    <div className="space-y-0.5">
                        {rows.map((r, i) => {
                            const msgs = Object.values(validationByRow?.[r.rowId] || {}).filter(Boolean);
                            if (!msgs.length) return null;
                            return (
                                <p key={r.rowId} className="text-[11px] text-red-500">
                                    Row {i + 1}: {msgs.join(' • ')}
                                </p>
                            );
                        })}
                    </div>
                )}

                <div className="flex items-center gap-6 text-[11px] text-gray-500 border-t border-gray-100 pt-3">
                    <span>Rows: <strong className="text-gray-700">{rows.length}</strong></span>
                    <span>Total hours: <strong className="text-gray-700">{Math.round(totalHours * 100) / 100}</strong></span>
                    <span>Total adjusted hours: <strong className="text-gray-700">{Math.round(totalAdjHours * 100) / 100}</strong></span>
                </div>
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════
//  STEP 2: SELECT EMPLOYEE(S)
// ═════════════════════════════════════════════════════════════════════════
const OTEmployeeSelectStep = ({
    mode, setMode,
    singleEmployee, setSingleEmployee, singleEmployeeError,
    multiSelected, toggleMulti, selectAllMulti, clearAllMulti,
    employees, employeesLoading,
}) => {
    const single = mode === 'single';
    const multi = mode === 'multiple';

    const selectedEmployees = useMemo(() => {
        const map = new Map(employees.map((e) => [e.employeeId, e]));
        return Array.from(multiSelected).map((id) => map.get(id)).filter(Boolean);
    }, [employees, multiSelected]);

    return (
        <div className="space-y-4">
            <div className={SECTION}>
                <div className={SECTION_HEADER}><Users size={15} /> Who is this OT for?</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button type="button" onClick={() => setMode('single')}
                        className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${single ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                        <span className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${single ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                            <UserCheck size={17} />
                        </span>
                        <div>
                            <p className={`text-sm font-semibold ${single ? 'text-primary-700' : 'text-gray-700'}`}>Single Employee</p>
                            <p className="text-[11px] text-gray-500 mt-0.5">Every OT row is recorded for one employee.</p>
                        </div>
                    </button>

                    <button type="button" onClick={() => setMode('multiple')}
                        className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${multi ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                        <span className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${multi ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                            <UsersRound size={17} />
                        </span>
                        <div>
                            <p className={`text-sm font-semibold ${multi ? 'text-primary-700' : 'text-gray-700'}`}>Multiple Employees</p>
                            <p className="text-[11px] text-gray-500 mt-0.5">Every OT row is applied to each selected employee.</p>
                        </div>
                    </button>
                </div>
            </div>

            {single && (
                <div className={SECTION}>
                    <div className={SECTION_HEADER}><User size={15} /> Employee</div>
                    <div className="max-w-md">
                        <span className={LABEL}>Employee <span className="text-red-500">*</span></span>
                        <div className={singleEmployeeError ? 'rounded-lg ring-1 ring-red-400' : ''}>
                            <EmployeeSearch value={singleEmployee}
                                onSelect={(emp) => setSingleEmployee(emp)}
                                employees={employees}
                                loading={employeesLoading} />
                        </div>
                        {singleEmployeeError && <p className="text-[11px] text-red-500 mt-1">{singleEmployeeError}</p>}
                    </div>
                </div>
            )}

            {multi && (
                <div className={SECTION}>
                    <div className="flex items-center justify-between">
                        <div className={SECTION_HEADER}><Users size={15} /> Select Employees</div>
                        <span className="text-[11px] text-gray-500">
                            <strong className="text-primary-700">{multiSelected.size}</strong> selected
                        </span>
                    </div>

                    {selectedEmployees.length > 0 && (
                        <SelectedChips employees={selectedEmployees}
                            onRemove={(id) => toggleMulti(selectedEmployees.find((e) => e.employeeId === id))}
                            onClear={clearAllMulti} />
                    )}

                    <MultiEmployeePicker employees={employees}
                        loading={employeesLoading}
                        selectedIds={multiSelected}
                        onToggle={toggleMulti}
                        onSelectAll={selectAllMulti}
                        onClearAll={clearAllMulti} />
                </div>
            )}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════
//  STEP 3: REVIEW & SAVE
// ═════════════════════════════════════════════════════════════════════════
const OTReviewStep = ({ rows, mode, singleEmployee, multiSelected, employees, otRates = {}, otRatesLoading = false }) => {
    const selectedMulti = useMemo(() => {
        const map = new Map(employees.map((e) => [e.employeeId, e]));
        return Array.from(multiSelected).map((id) => map.get(id)).filter(Boolean);
    }, [employees, multiSelected]);

    const targetEmployees =
        mode === 'single' ? (singleEmployee ? [singleEmployee] : []) : selectedMulti;

    const totalHours = rows.reduce(
        (sum, r) => sum + (Number(r.totalHours) || 0) * targetEmployees.length,
        0
    );
    const totalAdjHours = rows.reduce(
        (sum, r) => sum + (Number(r.adjHours) || 0) * targetEmployees.length,
        0
    );
    const totalRecords = rows.length * targetEmployees.length;

    // OT amount = adjusted hours × each employee's HED_OT_RATE.
    const rowAdjSum = rows.reduce((sum, r) => sum + (Number(r.adjHours) || 0), 0);
    const totalAmount = targetEmployees.reduce(
        (sum, e) => sum + rowAdjSum * (Number(otRates[String(e.employeeId)]) || 0),
        0
    );
    const missingRate = targetEmployees.filter(
        (e) => String(e.employeeId) in otRates && !Number(otRates[String(e.employeeId)])
    );

    return (
        <div className="space-y-4">
            <div className={SECTION}>
                <div className={SECTION_HEADER}>
                    <Clock size={15} /> OT Details ({rows.length} row{rows.length === 1 ? '' : 's'})
                </div>
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                                <th className="px-3 py-2 text-left font-semibold">#</th>
                                {/* <th className="px-3 py-2 text-left font-semibold">Period</th> */}
                                <th className="px-3 py-2 text-left font-semibold">Date</th>
                                <th className="px-3 py-2 text-left font-semibold">Type</th>
                                <th className="px-3 py-2 text-left font-semibold">Time</th>
                                <th className="px-3 py-2 text-left font-semibold">Hrs</th>
                                <th className="px-3 py-2 text-left font-semibold">Rate</th>
                                <th className="px-3 py-2 text-left font-semibold">Adjusted OT Hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => {
                                // const period = payrollPeriods.find(
                                //     (p) => String(p.id) === String(r.payrollPeriodId)
                                // );
                                return (
                                    <tr key={r.rowId} className="border-b border-gray-100 last:border-0">
                                        <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                                        {/* <td className="px-3 py-2 text-gray-700">{period ? formatPeriodLabel(period) : '—'}</td> */}
                                        <td className="px-3 py-2 text-gray-700">{formatDate(r.otDate)}</td>
                                        <td className="px-3 py-2 text-gray-700">{OT_TYPE_LABEL(r.otType)}</td>
                                        <td className="px-3 py-2 text-gray-600">{r.startTime} – {r.endTime}</td>
                                        <td className="px-3 py-2 text-gray-700">{r.totalHours || '—'}</td>
                                        <td className="px-3 py-2 text-gray-700">{r.otRate || '—'}</td>
                                        <td className="px-3 py-2 text-primary-700 font-medium">{r.adjHours || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={SECTION}>
                <div className="flex items-center justify-between">
                    <div className={SECTION_HEADER}><Users size={15} /> Employees ({targetEmployees.length})</div>
                    <span className="text-[11px] text-gray-500">
                        Mode: <strong className="text-gray-700">{mode === 'single' ? 'Single' : 'Multiple'}</strong>
                    </span>
                </div>
                {targetEmployees.length === 0 ? (
                    <div className="py-6 text-center text-xs text-gray-400">No employees selected</div>
                ) : (
                    <div className="max-h-[280px] overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-100">
                        {targetEmployees.map((emp) => (
                            <div key={emp.employeeId} className="flex items-center gap-3 px-3 py-2">
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-[10px] font-semibold text-white">
                                    {(emp.name || '?').charAt(0)}
                                </div>
                                <span className="text-[11px] font-mono text-gray-400 w-20 shrink-0">{emp.serviceNo || '—'}</span>
                                <span className="text-xs text-gray-800 truncate flex-1">{emp.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-primary-50 border border-primary-100 rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-primary-700 text-sm font-semibold">
                    <Info size={15} /> Grand Total
                </div>
                <div className="flex items-center gap-6 text-xs">
                    <div>
                        <span className="text-primary-500">OT rows: </span>
                        <strong className="text-primary-800">{rows.length}</strong>
                    </div>
                    <div>
                        <span className="text-primary-500">Employees: </span>
                        <strong className="text-primary-800">{targetEmployees.length}</strong>
                    </div>
                    <div>
                        <span className="text-primary-500">Records: </span>
                        <strong className="text-primary-800">{totalRecords}</strong>
                    </div>
                    <div>
                        <span className="text-primary-500">Total Hours: </span>
                        <strong className="text-primary-800">{Math.round(totalHours * 100) / 100}</strong>
                    </div>
                    <div>
                        <span className="text-primary-500">Total Adjusted Hours: </span>
                        <strong className="text-primary-800">{Math.round(totalAdjHours * 100) / 100}</strong>
                    </div>
                    <div>
                        <span className="text-primary-500">Total Amount: </span>
                        <strong className="text-primary-800">
                            {otRatesLoading ? 'Loading OT rates…' : (Math.round(totalAmount * 100) / 100).toLocaleString()}
                        </strong>
                    </div>
                    {missingRate.length > 0 && (
                        <div className="w-full text-[11px] text-red-600">
                            No OT rate set for: {missingRate.map((e) => e.name || e.serviceNo || e.employeeId).join(', ')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════
//  ADD TAB — 3-step wizard (multi-row × single/multi employees)
// ═════════════════════════════════════════════════════════════════════════
const OTBatchAddTab = () => {
    const dispatch = useDispatch();
    const { employees: reduxEmployees, loading: employeesLoading } =
        useSelector((s) => s.employees || { employees: [], loading: false });
    // const payrollPeriods = useSelector(selectOTEntryPeriods);
    // const periodsLoading = useSelector(selectOTEntryPeriodsLoading);

    const [employees, setEmployees] = useState([]);

    const [step, setStep] = useState(1);

    // Step 1
    const [rows, setRows] = useState([emptyOTRow()]);
    const [validationByRow, setValidationByRow] = useState({});

    // Step 2
    const [mode, setMode] = useState('single');
    const [singleEmployee, setSingleEmployee] = useState(null);
    const [singleEmployeeError, setSingleEmployeeError] = useState(null);
    const [multiSelected, setMultiSelected] = useState(() => new Set());

    // Submit
    const [submitting, setSubmitting] = useState(false);
    const [results, setResults] = useState(null);
    const [rateError, setRateError] = useState(null);

    // Each employee's HED_OT_RATE (EMP/GetEmployeebyID) — for p_otAmount.
    const otRates = useSelector(selectEmployeeOtRates);
    const otRatesLoading = useSelector(selectOtRatesLoading);

    useEffect(() => {
        dispatch(fetchAllEmployees());
        // if (!payrollPeriods.length) dispatch(fetchOTEntryPayrollPeriods());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        setEmployees(mapReduxEmployees(reduxEmployees));
    }, [reduxEmployees]);

    const toggleMulti = (emp) => {
        if (!emp) return;
        setMultiSelected((prev) => {
            const next = new Set(prev);
            if (next.has(emp.employeeId)) next.delete(emp.employeeId);
            else next.add(emp.employeeId);
            return next;
        });
    };
    const selectAllMulti = (list) => {
        setMultiSelected((prev) => {
            const next = new Set(prev);
            list.forEach((e) => next.add(e.employeeId));
            return next;
        });
    };
    const clearAllMulti = () => setMultiSelected(new Set());

    // ── validation ──────────────────────────────────────────────────────────
    const validateAllRows = () => {
        const byRow = {};
        let ok = true;
        rows.forEach((r) => {
            const v = validateOTRow(r);
            if (Object.keys(v).length) ok = false;
            byRow[r.rowId] = v;
        });
        setValidationByRow(byRow);
        return ok;
    };

    const validateEmployees = () => {
        if (mode === 'single') {
            if (!singleEmployee) {
                setSingleEmployeeError('Employee is required');
                return false;
            }
            setSingleEmployeeError(null);
            return true;
        }
        return multiSelected.size > 0;
    };

    // ── navigation ──────────────────────────────────────────────────────────
    const goNext = () => {
        if (step === 1) {
            if (!validateAllRows()) return;
            setStep(2);
        } else if (step === 2) {
            if (!validateEmployees()) return;
            setStep(3);
        }
    };
    const goBack = () => { if (step > 1) setStep(step - 1); };

    const handleReset = () => {
        setStep(1);
        setRows([emptyOTRow()]);
        setValidationByRow({});
        setMode('single');
        setSingleEmployee(null);
        setSingleEmployeeError(null);
        setMultiSelected(new Set());
        setResults(null);
        dispatch(clearOTEntryStatus());
    };

    const targetEmployees = useMemo(() => {
        if (mode === 'single') return singleEmployee ? [singleEmployee] : [];
        const map = new Map(employees.map((e) => [e.employeeId, e]));
        return Array.from(multiSelected).map((id) => map.get(id)).filter(Boolean);
    }, [mode, singleEmployee, employees, multiSelected]);

    const totalCombos = rows.length * targetEmployees.length;

    // Load the chosen employees' OT rates when the review step opens.
    useEffect(() => {
        if (step === 3 && targetEmployees.length) {
            dispatch(fetchEmployeeOtRates(targetEmployees.map((e) => e.employeeId)));
        }
    }, [step, targetEmployees, dispatch]);

    // ── submit ──────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        const okRows = validateAllRows();
        const okEmp = validateEmployees();
        if (!okRows) { setStep(1); return; }
        if (!okEmp) { setStep(2); return; }

        setSubmitting(true);
        setResults(null);
        setRateError(null);
        dispatch(clearOTEntryStatus());

        // p_otAmount needs every employee's OT rate — make sure they are loaded.
        let rates = otRates;
        try {
            rates = await dispatch(
                fetchEmployeeOtRates(targetEmployees.map((e) => e.employeeId))
            ).unwrap();
        } catch { /* use what is already loaded */ }
        const noRate = targetEmployees.filter((e) => !Number(rates[String(e.employeeId)]));
        if (noRate.length) {
            setRateError(
                `No OT rate set for: ${noRate.map((e) => e.name || e.serviceNo || e.employeeId).join(', ')}. ` +
                'Set the OT rate on the employee record first.'
            );
            setSubmitting(false);
            return;
        }

        const combos = [];
        rows.forEach((r) => {
            targetEmployees.forEach((emp) => {
                combos.push({
                    rowId: r.rowId,
                    employeeId: emp.employeeId,
                    name: emp.name,
                    serviceNo: emp.serviceNo,
                    otDate: r.otDate,
                    payload: {
                        employeeId: emp.employeeId,
                        // payrollPeriodId: r.payrollPeriodId,
                        otDate: r.otDate,
                        otType: r.otType,
                        startTime: r.startTime,
                        endTime: r.endTime,
                        totalHours: Number(r.totalHours) || 0,

                        othRate: Number(r.otRate) || 0,
                        otRate: Number(rates[String(emp.employeeId)]) || 0,
                        adjHours: Number(r.adjHours) || 0,

                        otAmount: Math.round(
                            (Number(r.adjHours) || 0) * Number(rates[String(emp.employeeId)]) * 100
                        ) / 100,
                        isActive: true,
                    },
                });
            });
        });

        const settled = await Promise.allSettled(
            combos.map((c) => dispatch(addOvertime(c.payload)).unwrap())
        );

        const rowResults = settled.map((s, i) => ({
            ...combos[i],
            ok: s.status === 'fulfilled',
            error:
                s.status === 'rejected'
                    ? s.reason?.message || String(s.reason) || 'Failed to save'
                    : null,
        }));

        setSubmitting(false);
        setResults(rowResults);

        const failed = rowResults.filter((r) => !r.ok);
        if (failed.length > 0 && mode === 'multiple') {
            setMultiSelected(new Set(failed.map((r) => r.employeeId)));
        }
    };

    const savedCount = results ? results.filter((r) => r.ok).length : 0;
    const failedCount = results ? results.filter((r) => !r.ok).length : 0;
    const allDone = results && failedCount === 0;

    return (
        <div className="space-y-4">
            <div className={SECTION}>
                <div className="flex items-center justify-between">
                    <Stepper step={step} />
                    <button type="button" onClick={handleReset} disabled={submitting}
                        className="flex items-center gap-2 h-8 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50">
                        <RotateCcw size={13} /> Start over
                    </button>
                </div>
            </div>

            {rateError && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-700">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{rateError}</span>
                </div>
            )}

            {results && (
                <div className={`flex items-start gap-2 px-4 py-3 rounded-lg text-sm ${failedCount ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' : 'bg-green-50 border border-green-200 text-green-700'}`}>
                    {failedCount ? <AlertCircle size={16} className="mt-0.5 shrink-0" /> : <Check size={16} className="mt-0.5 shrink-0" />}
                    <div className="flex-1">
                        <p className="font-medium">
                            {savedCount} of {savedCount + failedCount} record{savedCount + failedCount === 1 ? '' : 's'} saved
                            {failedCount ? ` — ${failedCount} failed` : ' successfully'}.
                        </p>
                        {failedCount > 0 && (
                            <ul className="mt-1 text-[11px] space-y-0.5">
                                {results.filter((r) => !r.ok).slice(0, 8).map((r, i) => (
                                    <li key={`${r.rowId}-${r.employeeId}-${i}`}>
                                        <span className="font-mono">{r.serviceNo}</span> {r.name} — {formatDate(r.otDate)}: {r.error}
                                    </li>
                                ))}
                                {failedCount > 8 && <li>…and {failedCount - 8} more</li>}
                            </ul>
                        )}
                        {allDone && (
                            <button type="button" onClick={handleReset}
                                className="mt-2 text-xs font-semibold text-green-700 underline hover:text-green-900">
                                Add another batch
                            </button>
                        )}
                    </div>
                </div>
            )}

            {step === 1 && (
                <OTDetailsStep rows={rows} setRows={setRows}
                    validationByRow={validationByRow} />
            )}

            {step === 2 && (
                <OTEmployeeSelectStep
                    mode={mode} setMode={setMode}
                    singleEmployee={singleEmployee}
                    setSingleEmployee={(emp) => {
                        setSingleEmployee(emp);
                        if (emp) setSingleEmployeeError(null);
                    }}
                    singleEmployeeError={singleEmployeeError}
                    multiSelected={multiSelected}
                    toggleMulti={toggleMulti}
                    selectAllMulti={selectAllMulti}
                    clearAllMulti={clearAllMulti}
                    employees={employees}
                    employeesLoading={employeesLoading} />
            )}

            {step === 3 && (
                <OTReviewStep rows={rows} mode={mode}
                    singleEmployee={singleEmployee}
                    multiSelected={multiSelected}
                    employees={employees}
                    otRates={otRates}
                    otRatesLoading={otRatesLoading} />
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
                <div className="text-xs text-gray-500">
                    {step === 1 && `Step 1 of 3 — Enter OT details (${rows.length} row${rows.length === 1 ? '' : 's'})`}
                    {step === 2 && `Step 2 of 3 — Select employee${mode === 'multiple' ? 's' : ''}${mode === 'multiple' ? ` (${multiSelected.size} selected)` : ''}`}
                    {step === 3 && `Step 3 of 3 — Review ${totalCombos} record${totalCombos === 1 ? '' : 's'} and save`}
                </div>
                <div className="flex items-center gap-2">
                    {step > 1 && (
                        <button type="button" onClick={goBack} disabled={submitting}
                            className="flex items-center gap-2 h-9 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50">
                            <ArrowLeft size={14} /> Back                        </button>
                    )}
                    {step < 3 && (
                        <button type="button" onClick={goNext}
                            className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors">
                            Next <ArrowRight size={14} />
                        </button>
                    )}
                    {step === 3 && !allDone && (
                        <button type="button" onClick={handleSubmit}
                            disabled={submitting || totalCombos === 0}
                            className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            Save {totalCombos} Record{totalCombos === 1 ? '' : 's'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════
//  SHARED FORM (single-record — used by Update tab)
// ═════════════════════════════════════════════════════════════════════════
const OTForm = ({
    form, setForm, setField, validation,
    employees, employeesLoading,
    selectedEmployee, onEmployeeSelect, disableEmployee = false,
}) => {
    useEffect(() => {
        const hrs = calcHours(form.startTime, form.endTime);
        setForm((prev) => {
            const nextHours = hrs > 0 ? String(hrs) : '';
            if (prev.totalHours === nextHours) return prev;
            return { ...prev, totalHours: nextHours };
        });
    }, [form.startTime, form.endTime]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const hrs = Number(form.totalHours) || 0;
        const rate = Number(form.otRate) || 0;
        if (hrs > 0 && rate > 0) {
            setForm((prev) => ({
                ...prev,
                otAmount: String(Math.round(hrs * rate * 100) / 100),
            }));
        }
    }, [form.totalHours, form.otRate]); // eslint-disable-line react-hooks/exhaustive-deps

    // const selectedPeriod = useMemo(
    //     () => payrollPeriods.find((p) => String(p.id) === String(form.payrollPeriodId)),
    //     [payrollPeriods, form.payrollPeriodId]
    // );
    const errCls = (k) => (validation[k] ? 'border-red-400 focus:border-red-500' : '');

    return (
        <div className="space-y-4">
            <div className={SECTION}>
                <div className={SECTION_HEADER}><User size={15} /> Employee &amp; Period</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <span className={LABEL}>Employee <span className="text-red-500">*</span></span>
                        <div className={validation.employeeId ? 'rounded-lg ring-1 ring-red-400' : ''}>
                            <EmployeeSearch value={selectedEmployee}
                                onSelect={onEmployeeSelect}
                                employees={employees}
                                loading={employeesLoading}
                                disabled={disableEmployee} />
                        </div>
                        {validation.employeeId && <p className="text-[11px] text-red-500 mt-1">{validation.employeeId}</p>}
                    </div>
                    {/* <div>
                        <span className={LABEL}>Payroll Period <span className="text-red-500">*</span></span>
                        <PeriodSelect value={form.payrollPeriodId}
                            onChange={(v) => setField('payrollPeriodId', v)}
                            className={`${FIELD} ${errCls('payrollPeriodId')}`}
                            periods={payrollPeriods}
                            loading={periodsLoading}
                            loadingLabel="Loading periods..."
                            emptyLabel="Select payroll period" />
                        {validation.payrollPeriodId && <p className="text-[11px] text-red-500 mt-1">{validation.payrollPeriodId}</p>}
                    </div> */}
                    <div>
                        <span className={LABEL}>OT Date <span className="text-red-500">*</span></span>
                        <div className="relative">
                            <Calendar size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <DatePickerWithFormat value={form.otDate}
                                onChange={(v) => setField('otDate', v)}
                                className={`${FIELD} pl-2.5 ${errCls('otDate')}`} />
                        </div>
                        {validation.otDate && <p className="text-[11px] text-red-500 mt-1">{validation.otDate}</p>}
                    </div>
                </div>
                {/* {selectedPeriod && (
                    <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-2 rounded-lg text-[11px]">
                        <Info size={13} className="mt-0.5 shrink-0" />
                        <span>OT window for <strong>{selectedPeriod.name}</strong>: {selectedPeriod.otStartDate || '—'} → {selectedPeriod.otEndDate || '—'}</span>
                    </div>
                )} */}
            </div>

            <div className={SECTION}>
                <div className={SECTION_HEADER}><Clock size={15} /> OT Details</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                    <span className={LABEL}>Shift <span className="text-red-500">*</span></span>
                    <select value={form.otType}
                        onChange={(e) => {
                            const next = e.target.value;
                            setField('otType', next);
                            const suggestedStart = OT_TYPE_DEFAULT_START[next];
                            if (suggestedStart) {
                                const prevSuggested = OT_TYPE_DEFAULT_START[form.otType];
                                const shouldFillStart = !form.startTime || form.startTime === prevSuggested;
                                if (shouldFillStart) setField('startTime', suggestedStart);
                            }
                            const suggestedRate = OT_TYPE_DEFAULT_RATE[next];
                            if (suggestedRate !== undefined) {
                                const prevRate = OT_TYPE_DEFAULT_RATE[form.otType];
                                const shouldFillRate = !form.otRate || Number(form.otRate) === Number(prevRate);
                                if (shouldFillRate) setField('otRate', String(suggestedRate));
                            }
                        }}
                        className={`${FIELD} ${errCls('otType')}`}>
                        {OT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    {validation.otType && <p className="text-[11px] text-red-500 mt-1">{validation.otType}</p>}
                    </div>
                    <div>
                        <span className={LABEL}>Start Time <span className="text-red-500">*</span></span>
                        <TimeField24 value={form.startTime}
                            onChange={(v) => setField('startTime', v)}
                            hasError={!!validation.startTime} />
                        {validation.startTime && <p className="text-[11px] text-red-500 mt-1">{validation.startTime}</p>}
                    </div>
                    <div>
                        <span className={LABEL}>End Time <span className="text-red-500">*</span></span>
                        <TimeField24 value={form.endTime}
                            onChange={(v) => setField('endTime', v)}
                            hasError={!!validation.endTime} />
                        {validation.endTime && <p className="text-[11px] text-red-500 mt-1">{validation.endTime}</p>}
                    </div>
                    <div>
                        <span className={LABEL}>Total Hours <span className="text-red-500">*</span></span>
                        <input type="number" step="0.25" min="0" value={form.totalHours}
                            onChange={(e) => setField('totalHours', e.target.value)}
                            className={`${FIELD} ${errCls('totalHours')}`} />
                        {validation.totalHours
                            ? <p className="text-[11px] text-red-500 mt-1">{validation.totalHours}</p>
                            : <p className="text-[11px] text-gray-400 mt-1">Auto-calculated (handles overnight).</p>}
                    </div>
                </div>
            </div>

            <div className={SECTION}>
                <div className={SECTION_HEADER}><DollarSign size={15} /> Pay &amp; Status</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <span className={LABEL}>OT Rate <span className="text-red-500">*</span></span>
                        <input type="number" step="0.01" min="0" value={form.otRate}
                            readOnly disabled
                            className={`${FIELD} disabled:bg-gray-50 disabled:text-gray-500 cursor-not-allowed`} />
                        {validation.otRate && <p className="text-[11px] text-red-500 mt-1">{validation.otRate}</p>}
                    </div>
                    <div>
                        <span className={LABEL}>OT Amount</span>
                        <input type="number" step="0.01" min="0" value={form.otAmount}
                            onChange={(e) => setField('otAmount', e.target.value)}
                            className={FIELD} />
                        <p className="text-[11px] text-gray-400 mt-1">Auto = hours × rate.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Status badge ───────────────────────────────────────────────────────────
const StatusBadge = ({ isApproved }) =>
    isApproved ? (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-800">Approved</span>
    ) : (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-yellow-100 text-yellow-800">Pending</span>
    );

// ═════════════════════════════════════════════════════════════════════════
//  UPDATE TAB (unchanged behaviour)
// ═════════════════════════════════════════════════════════════════════════
const OTUpdateTab = () => {
    const dispatch = useDispatch();
    const { employees: reduxEmployees, loading: employeesLoading } =
        useSelector((s) => s.employees || { employees: [], loading: false });
    // const payrollPeriods = useSelector(selectOTEntryPeriods);
    // const periodsLoading = useSelector(selectOTEntryPeriodsLoading);
    const submitting = useSelector(selectOTEntrySubmitting);
    const errorMsg = useSelector(selectOTEntryError);
    const overtimeList = useSelector(selectOTEntryList);
    const listLoading = useSelector(selectOTEntryListLoading);

    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [validation, setValidation] = useState({});
    const [editingRowId, setEditingRowId] = useState(null);

    const [filterEmployeeObj, setFilterEmployeeObj] = useState(null);
    // const [filterPeriod, setFilterPeriod] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const [page, setPage] = useState(1);
    const PAGE_SIZE = 8;

    useEffect(() => {
        dispatch(fetchAllEmployees());
        // if (!payrollPeriods.length) dispatch(fetchOTEntryPayrollPeriods());
        dispatch(fetchOvertimeList());
    }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setEmployees(mapReduxEmployees(reduxEmployees));
    }, [reduxEmployees]);

    useEffect(() => {
        if (!editingRowId) return;
        if (selectedEmployee) return;
        if (!form.employeeId) return;
        if (!employees.length) return;
        const match = employees.find((e) => employeeMatchesId(e, form.employeeId));
        if (match) setSelectedEmployee(match);
    }, [editingRowId, employees, form.employeeId, selectedEmployee]);

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setValidation((prev) => ({ ...prev, [key]: undefined }));
        if (errorMsg) dispatch(clearOTEntryStatus());
    };

    const handleEmployeeSelect = (emp) => {
        setSelectedEmployee(emp);
        setField('employeeId', emp?.employeeId || '');
    };

    const handleEditRow = (row) => {
        setEditingRowId(row.id);
        setForm({
            overtimeId: String(row.id || ''),
            payrollId: String(row.payrollId || ''),
            employeeId: String(row.employeeId || ''),
            payrollPeriodId: String(row.payrollPeriodId || ''),
            otDate: row.otDate || '',
            otType: row.otType || 'T',
            startTime: row.startTime || '',
            endTime: row.endTime || '',
            totalHours: String(row.hours || ''),
            otRate: String(row.rate || ''),
            otAmount: String(row.amount || ''),
            isActive: true,
        });
        setValidation({});
        dispatch(clearOTEntryStatus());

        const targetId = row.employeeId;
        const match = employees.find((e) => employeeMatchesId(e, targetId));
        setSelectedEmployee(match || null);
    };

    const cancelEdit = () => {
        setEditingRowId(null);
        setForm(initialForm);
        setSelectedEmployee(null);
        setValidation({});
        dispatch(clearOTEntryStatus());
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!editingRowId) return;
        const v = validateForm(form);
        setValidation(v);
        if (Object.keys(v).length) return;

        const payload = {
            overtimeId: form.overtimeId,
            employeeId: form.employeeId,
            payrollPeriodId: form.payrollPeriodId,
            otDate: form.otDate,
            otType: form.otType,
            startTime: form.startTime,
            endTime: form.endTime,
            totalHours: Number(form.totalHours) || 0,
            otRate: Number(form.otRate) || 0,
            otAmount: Number(form.otAmount) || 0,
            payrollId: form.payrollId,
            isActive: true,
        };

        dispatch(updateOvertime(payload)).unwrap().then(() => {
            cancelEdit();
        }).catch(() => { });
    };

    const handleResetFilters = () => {
        setFilterEmployeeObj(null);
        // setFilterPeriod('');
        setSearchTerm('');
    };

    const employeeNameById = useMemo(() => {
        const m = new Map();
        employees.forEach((e) => m.set(String(e.employeeId), e.name));
        return m;
    }, [employees]);

    // const periodLabelById = useMemo(() => {
    //     const m = new Map();
    //     payrollPeriods.forEach((p) => m.set(String(p.id), formatPeriodLabel(p)));
    //     return m;
    // }, [payrollPeriods]);

    const visibleRows = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return overtimeList
            .filter((r) => !r.isApproved)
            .filter((r) => {
                if (filterEmployeeObj && String(r.employeeId) !== String(filterEmployeeObj.employeeId)) return false;
                // if (filterPeriod && String(r.payrollPeriodId) !== String(filterPeriod)) return false;
                if (term) {
                    const name = employeeNameById.get(String(r.employeeId)) || '';
                    const hay = `${r.id} ${r.employeeId} ${name} ${r.otDate} ${OT_TYPE_LABEL(r.otType)}`.toLowerCase();
                    if (!hay.includes(term)) return false;
                }
                return true;
            });
    }, [overtimeList, filterEmployeeObj, searchTerm, employeeNameById]);

    useEffect(() => { setPage(1); }, [filterEmployeeObj, searchTerm]);

    const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);

    useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

    const paginatedRows = useMemo(
        () => visibleRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
        [visibleRows, currentPage]
    );

    if (editingRowId) {
        return (
            <form id="ot-update-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center justify-between bg-blue-50 border border-blue-100 text-blue-800 px-4 py-2.5 rounded-lg text-xs">
                    <span>
                        Editing OT record <strong>#{editingRowId}</strong>
                        {selectedEmployee ? ` — ${selectedEmployee.name}` : ''}
                    </span>
                    <button type="button" onClick={cancelEdit} className="text-blue-700 hover:text-blue-900 font-semibold">
                        Back to list
                    </button>
                </div>

                <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={cancelEdit} disabled={submitting}
                        className="flex items-center gap-2 h-9 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50">
                        <X size={14} /> Cancel
                    </button>
                    <button type="submit" disabled={submitting}
                        className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Update OT Entry
                    </button>
                </div>

                <OTForm form={form} setForm={setForm} setField={setField} validation={validation}
                    employees={employees} employeesLoading={employeesLoading}
                    selectedEmployee={selectedEmployee} onEmployeeSelect={handleEmployeeSelect}
                    disableEmployee />
            </form>
        );
    }

    return (
        <div className="space-y-4">
            <div className={SECTION}>
                <div className="flex items-center justify-between">
                    <div className={SECTION_HEADER}><List size={15} /> Pending OT Records</div>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={handleResetFilters}
                            className="flex items-center gap-2 h-8 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors">
                            <RotateCcw size={13} /> Reset Filters
                        </button>
                        <button type="button" onClick={() => dispatch(fetchOvertimeList())} disabled={listLoading}
                            className="flex items-center gap-2 h-8 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50">
                            {listLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                            Refresh
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <EmployeeSearch value={filterEmployeeObj}
                        onSelect={setFilterEmployeeObj}
                        employees={employees}
                        loading={employeesLoading}
                        placeholder="All employees..." />
                    {/* <PeriodSelect value={filterPeriod}
                        onChange={setFilterPeriod}
                        periods={payrollPeriods}
                        getLabel={(p) => `${p.name} (${p.code})`}
                        loading={periodsLoading}
                        loadingLabel="Loading periods..."
                        emptyLabel="All periods" /> */}
                    <div className="flex items-center text-[11px] text-gray-500">
                        <Filter size={12} className="mr-1" />
                        {visibleRows.length} pending record{visibleRows.length === 1 ? '' : 's'}
                    </div>
                </div>

                {listLoading ? (
                    <div className="flex items-center justify-center py-10">
                        <Loader2 size={22} className="animate-spin text-primary-600" />
                    </div>
                ) : visibleRows.length === 0 ? (
                    <div className="py-10 text-center text-xs text-gray-400">
                        No pending OT records. Approved records can no longer be edited.
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto -mx-5">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                                        <th className="px-5 py-2.5 text-left font-semibold">Employee</th>
                                        {/* <th className="px-5 py-2.5 text-left font-semibold">Period</th> */}
                                        <th className="px-5 py-2.5 text-left font-semibold">Date</th>
                                        <th className="px-5 py-2.5 text-left font-semibold">Type</th>
                                        <th className="px-5 py-2.5 text-left font-semibold">Start–End</th>
                                        <th className="px-5 py-2.5 text-left font-semibold">Hours</th>
                                        <th className="px-5 py-2.5 text-left font-semibold">Rate</th>
                                        <th className="px-5 py-2.5 text-left font-semibold">Amount</th>
                                        <th className="px-5 py-2.5 text-left font-semibold">Status</th>
                                        <th className="px-5 py-2.5 text-right font-semibold"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRows.map((r) => (
                                        <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="px-5 py-2.5 text-gray-800">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-[10px] font-semibold text-white">
                                                        {(employeeNameById.get(String(r.employeeId)) || '?').charAt(0)}
                                                    </div>
                                                    <span className="truncate max-w-[180px]">
                                                        {employeeNameById.get(String(r.employeeId)) || r.employeeId || '—'}
                                                    </span>
                                                </div>
                                            </td>
                                            {/* <td className="px-5 py-2.5 text-gray-700">
                                                <span className="truncate max-w-[180px] inline-block align-middle">
                                                    {periodLabelById.get(String(r.payrollPeriodId)) || r.payrollPeriodId || '—'}
                                                </span>
                                            </td> */}
                                            <td className="px-5 py-2.5 text-gray-600">{formatDate(r.otDate)}</td>
                                            <td className="px-5 py-2.5 font-medium text-gray-800">{OT_TYPE_LABEL(r.otType)}</td>
                                            <td className="px-5 py-2.5 text-gray-600 text-xs">{r.startTime || '—'} – {r.endTime || '—'}</td>
                                            <td className="px-5 py-2.5 text-gray-700">{r.hours}</td>
                                            <td className="px-5 py-2.5 text-gray-700">{r.rate}</td>
                                            <td className="px-5 py-2.5 text-primary-700 font-medium">{r.amount}</td>
                                            <td className="px-5 py-2.5"><StatusBadge isApproved={r.isApproved} /></td>
                                            <td className="px-5 py-2.5 text-right">
                                                <button type="button" onClick={() => handleEditRow(r)}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-md text-xs font-medium hover:bg-blue-100">
                                                    <Pencil size={12} /> Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {visibleRows.length > 0 && pageCount > 1 && (
                            <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-between border-t border-gray-100">
                                <span className="text-xs text-gray-500">
                                    Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                                    {Math.min(currentPage * PAGE_SIZE, visibleRows.length)} of {visibleRows.length} entries
                                </span>
                                <div className="flex items-center gap-1">
                                    <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="h-7 min-w-[28px] px-2 rounded-md text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                                        Prev
                                    </button>
                                    {Array.from({ length: pageCount }, (_, i) => i + 1)
                                        .filter((p) => {
                                            if (pageCount <= 7) return true;
                                            if (currentPage <= 4) return p <= 5 || p === pageCount;
                                            if (currentPage >= pageCount - 3) return p === 1 || p >= pageCount - 4;
                                            return p === 1 || p === pageCount || Math.abs(p - currentPage) <= 1;
                                        })
                                        .map((p, i, arr) => (
                                            <React.Fragment key={p}>
                                                {i > 0 && p - arr[i - 1] > 1 && (
                                                    <span className="px-1 text-xs text-gray-400">…</span>
                                                )}
                                                <button type="button" onClick={() => setPage(p)}
                                                    className={`h-7 min-w-[28px] px-2 rounded-md text-xs font-semibold transition-colors ${p === currentPage
                                                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                                                        : 'text-gray-600 hover:bg-gray-100'
                                                        }`}>
                                                    {p}
                                                </button>
                                            </React.Fragment>
                                        ))}
                                    <button type="button" onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                                        disabled={currentPage === pageCount}
                                        className="h-7 min-w-[28px] px-2 rounded-md text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════
export default function OTEntry() {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('add');
    const isAddTab = activeTab === 'add';
    const isUpdateTab = activeTab === 'update';

    const successMsg = useSelector(selectOTEntrySuccess);
    const errorMsg = useSelector(selectOTEntryError);
    const successTimer = useRef(null);

    useEffect(() => {
        if (successMsg) {
            if (successTimer.current) clearTimeout(successTimer.current);
            successTimer.current = setTimeout(() => dispatch(clearOTEntryStatus()), 4000);
        }
        return () => { if (successTimer.current) clearTimeout(successTimer.current); };
    }, [successMsg, dispatch]);

    const handleTabChange = (tab) => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        dispatch(clearOTEntryStatus());
    };

    return (
        <div className="p-6 space-y-4">
            <Breadcrumb trail={['Overtime Management', 'OT Entry']} title="Overtime Entry" />

            {isUpdateTab && errorMsg && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{errorMsg}</span>
                </div>
            )}
            {isUpdateTab && successMsg && (
                <div className="flex items-start gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                    <Check size={16} className="mt-0.5 shrink-0" /> <span>{successMsg}</span>
                </div>
            )}

            <div className="border-b border-gray-200 bg-white rounded-t-xl px-4 pt-2">
                <nav className="flex gap-0" aria-label="Tabs">
                    <button type="button" onClick={() => handleTabChange('add')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isAddTab
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}>
                        <Plus size={16} /> Add OT Entry
                    </button>
                    <button type="button" onClick={() => handleTabChange('update')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isUpdateTab
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}>
                        <Pencil size={16} /> Update OT Entry
                    </button>
                </nav>
            </div>

            {isAddTab ? <OTBatchAddTab /> : <OTUpdateTab />}
        </div>
    );
}